/**
 * Composer Fast (Dax, 2026-10-31): bring in whole clips, split them, drag the handles, add sound, Export. The picture is cut
 * on keyframes and copied rather than re-encoded, so a 4K edit exports in seconds, and no analysis is needed first. A fast
 * edit is an ordinary draft, so the audio panel, the live mix and the preview are the Composer's own (useDraftDesk). Text and
 * exact cuts are the full Composer's, one click away.
 */
import { useMutation, useQuery } from '@tanstack/react-query';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { FAST_SPLIT_PARTS, snapEdge, type FastSplitParts } from '@studio/contracts';
import { Icon } from '@/components/icons';
import { Knob } from '@/components/knob';
import { PreviewScrubber } from '@/components/preview-scrubber';
import { Recents } from '@/components/recent-files';
import { RenderPill } from '@/components/render-pill';
import { RowMenu } from '@/components/row-menu';
import { SaveStatus } from '@/components/save-status';
import { TopBar } from '@/components/shell';
import { SlotThumb } from '@/components/slot-thumb';
import { Tool, ToolDivider, Toolbar } from '@/components/toolbar';
import { Waveform } from '@/components/waveform';
import { GAIN_MAX, GAIN_MIN, useDraftDesk } from '@/hooks/use-draft-desk';
import { fmtTime } from '@/lib/format';
import { queryClient, trpc } from '@/lib/trpc';

/** A fast edit survives a refresh, kept apart from the Composer's own. */
const FAST_DRAFT_KEY = 'studio-compose-fast-draft';
/** How often keyframes being read, a clip being prepared or an analysis is looked at again. */
const KEYFRAME_POLL_MS = 3000;
/** A segment's waveform on its card: the same height as the Composer's. */
const SEGMENT_WAVE_H = 46;
/**
 * A card's waveform shows its segment and this much either side: at least the minimum, or half the segment. A whole clip
 * on a card that size is about a second to the pixel, too coarse to place a handle. Each release centres it again.
 */
const WAVE_MARGIN_MIN_MS = 10 * 1000;
const WAVE_MARGIN_SHARE = 0.5;
/** Keyframe marks are drawn only when there are few enough on a card to tell apart. */
const KEY_MARKS_MAX = 40;

const clipLength = (ms: number | null) => (ms === null ? '' : fmtTime(ms, false));

export function ComposeFastPage() {
  const navigate = useNavigate();
  const search = useSearch({ from: '/_app/compose-fast' });
  const [draftId, setDraftIdState] = useState<string | null>(() => {
    if (search.draft) return search.draft;
    try {
      return sessionStorage.getItem(FAST_DRAFT_KEY);
    } catch {
      return null;
    }
  });
  const setDraftId = (id: string | null) => {
    setDraftIdState(id);
    try {
      if (id) sessionStorage.setItem(FAST_DRAFT_KEY, id);
      else sessionStorage.removeItem(FAST_DRAFT_KEY);
    } catch {
      // Storage unavailable: the page still works, it just will not survive a refresh.
    }
    // An address naming another edit would bring that one back on a reload.
    if (search.draft && search.draft !== id) void navigate({ to: '/compose-fast', search: {}, replace: true });
  };
  const {
    d, refresh, update, draftSave, render, setRenderId, stagedSlots, setStagedSlots, activeEdge, setActiveEdge, commitSlot, moveSlots, removeSlot, setSlot, totalMs,
    previewPlaying, positionMs, seekPreview, playPreview, pausePreview, stopPreview, videoRef, altVideoRef, frontVideo, onVideoPlay, onVideoPause, startPreview, previewSlot,
    audioDesk, audioPopups, history, panelNote, offByHand, clipTints, levelFresh, levelClips, draft,
  } = useDraftDesk({ draftId, freshDraftId: null });
  // An edit deleted elsewhere, in the Archive or another tab, leaves nothing to open.
  useEffect(() => {
    if (draft.error?.data?.code === 'NOT_FOUND') setDraftId(null);
  }, [draft.error]);

  // The segment the toolbar's Split acts on, the Split rollout, the two pickers, and clips being prepared.
  const [selected, setSelected] = useState(0);
  const [splitOpen, setSplitOpen] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [loadOpen, setLoadOpen] = useState(false);
  const [preparing, setPreparing] = useState<Set<string>>(new Set());
  const [dragIndex, setDragIndex] = useState<number | null>(null);
  const [dragOrder, setDragOrder] = useState<number[] | null>(null);
  const [problem, setProblem] = useState<string | null>(null);

  const clips = useQuery(trpc.fast.clips.queryOptions());
  const edits = useQuery(trpc.compose.list.queryOptions({ templates: false, cutMode: 'fast' }));
  const isFast = d?.cutMode === 'fast';
  const keyframes = useQuery(trpc.fast.keyframes.queryOptions({ draftId: draftId ?? '' }, {
    enabled: Boolean(draftId && isFast),
    refetchInterval: (q) => (preparing.size || q.state.data?.some((k) => k.unread || k.analysing) ? KEYFRAME_POLL_MS : false),
  }));
  const clipInfo = new Map((keyframes.data ?? []).map((k) => [k.assetId, k]));
  // An exact edit opened here by address belongs in the Composer; one remembered here has since moved there.
  useEffect(() => {
    if (!d || isFast) return;
    if (search.draft === d.id) void navigate({ to: '/compose', search: { draft: d.id }, replace: true });
    else setDraftId(null);
  }, [d?.id, isFast]);
  // A clip whose grade has moved on has finished preparing.
  useEffect(() => {
    if (!preparing.size) return;
    const left = new Set([...preparing].filter((id) => clipInfo.get(id)?.grade === 'prepare'));
    if (left.size !== preparing.size) setPreparing(left);
  }, [keyframes.data]);

  const afterEdit = async () => {
    await Promise.all([refresh(), queryClient.invalidateQueries({ queryKey: trpc.fast.keyframes.queryKey() })]);
  };
  const onError = (err: { message: string }) => setProblem(err.message);
  const create = useMutation(trpc.fast.create.mutationOptions({
    onSuccess: async (r) => {
      // The first clip is levelled as it opens, as every clip added after it is.
      levelFresh.current = r.draftId;
      setDraftId(r.draftId);
      setRenderId(null);
      setSelected(0);
      setAddOpen(false);
      await afterEdit();
    },
    onError,
  }));
  const addClip = useMutation(trpc.fast.addClip.mutationOptions({
    onSuccess: async () => {
      setAddOpen(false);
      await afterEdit();
    },
    onError,
  }));
  const split = useMutation(trpc.fast.split.mutationOptions({
    onSuccess: async (_r, v) => {
      setSplitOpen(false);
      setProblem(null);
      // The first part keeps the segment's row, so it is not new to the desk; it is levelled over its own length like the rest.
      const first = d?.slots[v.slotIndex]?.snippetId;
      await afterEdit();
      if (first && d?.autoLevel.clips) void levelClips(new Set([first]));
    },
    onError,
  }));
  const exportCut = useMutation(trpc.fast.exportCut.mutationOptions({ onSuccess: (r) => setRenderId(r.renderId), onError }));
  const analyse = useMutation(trpc.fast.analyse.mutationOptions({ onSuccess: afterEdit, onError }));
  const prepare = useMutation(trpc.fast.prepare.mutationOptions({ onError }));
  const toComposer = useMutation(trpc.fast.toComposer.mutationOptions({
    onSuccess: (r) => void navigate({ to: '/compose', search: { draft: r.draftId } }),
    onError,
  }));
  const discard = useMutation(trpc.compose.remove.mutationOptions({
    onSuccess: async () => {
      setDraftId(null);
      setRenderId(null);
      await queryClient.invalidateQueries({ queryKey: trpc.compose.list.queryKey() });
    },
    onError,
  }));

  const bringIn = (assetId: string) => {
    if (d && isFast) addClip.mutate({ draftId: d.id, assetId });
    else create.mutate({ assetId });
  };
  const splitSlot = (si: number, parts: FastSplitParts) => {
    if (d) split.mutate({ draftId: d.id, slotIndex: si, parts });
  };
  const unanalysed = (keyframes.data ?? []).filter((k) => !k.analysed && !k.analysing).length;
  const analysing = (keyframes.data ?? []).some((k) => k.analysing);
  const anyRead = (keyframes.data ?? []).some((k) => k.grid.points.length > 0);

  const clipPicker = (
    <div className="picker-list">
      {clips.data?.map((c) => (
        <button key={c.id} type="button" className="picker-row" disabled={create.isPending || addClip.isPending} onClick={() => bringIn(c.id)}>
          {c.posterUrl ? <img className="picker-thumb" src={c.posterUrl} alt="" /> : <span className="picker-thumb empty-poster" />}
          <span className="dd-label">{c.name}</span>
          <span className="sub dd-n">{clipLength(c.durationMs)}</span>
        </button>
      ))}
      {clips.data?.length === 0 && <span className="sub">Nothing here yet. Upload a clip first.</span>}
    </div>
  );

  return (
    <>
      <TopBar title="Composer Fast">
        {d && <span className="sub crumb compose-name">{d.name ?? 'Untitled edit'}</span>}
      </TopBar>
      <section className="content compose-content">
        {d && isFast && (
          <div className="toolstrip compose-toolstrip">
            <Toolbar dense>
              <Tool icon="undo" label="Undo" title="Undo the last change" disabled={!history.canUndo} onClick={history.undo} />
              <Tool icon="redo-arrow" label="Redo" title="Redo what was undone" disabled={!history.canRedo} onClick={history.redo} />
              <ToolDivider />
              <Tool icon="plus" label="New" title="Start a new edit" onClick={() => { setDraftId(null); setRenderId(null); }} />
              <SaveStatus state={draftSave} error={update.error?.message ?? null} />
              <Tool icon="folder" label="Load" title="Open a saved edit" active={loadOpen} onClick={() => setLoadOpen(true)} />
              <Tool icon="trash" label="Discard" title="Close this edit without keeping it" onClick={() => discard.mutate({ draftId: d.id })} />
              <ToolDivider />
              <Tool icon="plus" label="Add clip" title="Add a whole clip after the last" active={addOpen} onClick={() => setAddOpen(true)} />
              <Tool icon="scissors" label="Split" title={`Split segment ${selected + 1}`} active={splitOpen} disabled={!anyRead || !d.slots.length} onClick={() => setSplitOpen(!splitOpen)} />
              {splitOpen && (
                <span className="seg fast-split" role="group" aria-label={`Split segment ${selected + 1} into`}>
                  {FAST_SPLIT_PARTS.map((n) => <button key={n} type="button" disabled={split.isPending} onClick={() => splitSlot(selected, n)}>{n}</button>)}
                </span>
              )}
              <ToolDivider />
              <Tool icon="pulse" label={analysing ? 'Analysing…' : 'Analyse'} title="Send these clips to analysis, for search, tags and the AI tools. Cuts stay on keyframes" disabled={!unanalysed || analyse.isPending}
                onClick={() => analyse.mutate({ draftId: d.id })} />
              <Tool icon="compose" label="Full Composer" title="Open this edit in the full Composer, for text and exact cuts" disabled={toComposer.isPending}
                onClick={() => toComposer.mutate({ draftId: d.id })} />
            </Toolbar>
          </div>
        )}
        {problem && <div className="error" role="alert">{problem}</div>}

        {!draftId && (
          <div className="col">
            <div className="card pad fast-start">
              <h2>Bring in a clip</h2>
              {clipPicker}
            </div>
            <Recents moreTo="/archive" onOpen={(id) => { setDraftId(id); setRenderId(null); }} items={(edits.data ?? []).map((x) => ({ id: x.id, name: x.name ?? 'Untitled edit', posterUrl: x.posterUrl }))} />
          </div>
        )}

        {d && isFast && (
          <div className="compose-body">
            <div className="compose-left">
              <div className="slot-grid z0">
                {(dragOrder ?? d.slots.map((_, i) => i)).map((si, vi) => {
                  const slot = d.slots[si];
                  if (!slot) return null;
                  const meta = d.slotMeta[si];
                  const order = dragOrder ?? d.slots.map((_, i) => i);
                  const info = meta ? clipInfo.get(meta.assetId) : undefined;
                  const staged = stagedSlots[si] ?? null;
                  const shown = staged ?? slot;
                  const clipMs = meta?.assetMs ?? slot.outMs;
                  const margin = Math.max(WAVE_MARGIN_MIN_MS, (slot.outMs - slot.inMs) * WAVE_MARGIN_SHARE);
                  const windowStart = Math.max(0, slot.inMs - margin);
                  const windowEnd = Math.min(clipMs, slot.outMs + margin);
                  const marks = (info?.grid.points ?? []).map((p) => p.startMs).filter((t) => t > windowStart && t < windowEnd);
                  const isPreparing = meta ? preparing.has(meta.assetId) : false;
                  return (
                    <div key={`${slot.snippetId}-${si}`} className={`slot-card${selected === si ? ' selected' : ''}${dragIndex === si ? ' dragging' : ''}`}
                      onClick={() => setSelected(si)}
                      onDragOver={(e) => {
                        e.preventDefault();
                        if (dragIndex === null) return;
                        const cur = order.indexOf(dragIndex);
                        if (cur === vi) return;
                        const next = [...order];
                        next.splice(cur, 1);
                        next.splice(vi, 0, dragIndex);
                        setDragOrder(next);
                      }}
                      onDrop={(e) => e.preventDefault()}>
                      <div className="slot-role">
                        <b title={meta?.title ?? meta?.assetName ?? 'Clip removed'}>{meta?.title ?? meta?.assetName ?? 'Clip removed'}</b>
                        <span className="slot-grip" draggable role="button" aria-label="Drag to reorder" title="Drag to reorder"
                          onDragStart={(e) => { setDragIndex(si); setDragOrder(order); e.dataTransfer.effectAllowed = 'move'; }}
                          onDragEnd={(e) => {
                            if (e.dataTransfer.dropEffect !== 'none' && dragOrder) moveSlots(dragOrder);
                            setDragIndex(null);
                            setDragOrder(null);
                          }}>
                          <Icon name="grip" />
                        </span>
                        <span className="sub slot-len">{((shown.outMs - shown.inMs) / 1000).toFixed(1)} s</span>
                      </div>
                      <button type="button" className="slot-th" onClick={() => startPreview(si, true)} aria-label={`Play segment ${vi + 1}`}>
                        <SlotThumb proxyUrl={meta?.proxyUrl ?? null} posterUrl={meta?.posterUrl ?? null}
                          timeMs={staged && activeEdge[si] === 'out' ? Math.max(staged.inMs, staged.outMs - 100) : shown.inMs} />
                        <span className="slot-dur mono">{((shown.outMs - shown.inMs) / 1000).toFixed(1)}s</span>
                      </button>
                      <div className="slot-track clip">
                        {meta && (
                          <div className="slot-wave" title={panelNote('clips') ?? undefined}>
                            <Waveform peaks={meta.peaks} durationMs={clipMs} windowStartMs={windowStart} windowEndMs={windowEnd}
                              regions={[]} words={meta.words} showWords={false} sceneCuts={marks.length <= KEY_MARKS_MAX ? marks : []} height={SEGMENT_WAVE_H}
                              ranges={[{ id: 'slot', inMs: slot.inMs, outMs: slot.outMs, colour: clipTints[si]?.accent ?? 'var(--accent)', excluded: false }]}
                              selectedId="slot" staged={staged} playheadMs={null}
                              onEdge={(which, ms, done) => {
                                const cur = stagedSlots[si] ?? { inMs: slot.inMs, outMs: slot.outMs };
                                const grid = info?.grid;
                                const next = which === 'in'
                                  ? { ...cur, inMs: grid?.points.length ? snapEdge(grid, 'in', ms, cur.outMs) : Math.min(ms, cur.outMs - 1) }
                                  : { ...cur, outMs: grid?.points.length ? snapEdge(grid, 'out', ms, cur.inMs) : Math.max(ms, cur.inMs + 1) };
                                if (done) {
                                  commitSlot(si, next);
                                  setActiveEdge((e) => {
                                    const { [si]: gone, ...rest } = e;
                                    void gone;
                                    return rest;
                                  });
                                } else {
                                  setActiveEdge((e) => (e[si] === which ? e : { ...e, [si]: which }));
                                  setStagedSlots((s) => ({ ...s, [si]: next }));
                                }
                              }} />
                          </div>
                        )}
                        <div className="row slot-tools">
                          <Toolbar dense>
                            <Tool icon="scissors" title="Split this segment" disabled={!info?.grid.points.length}
                              onClick={() => { setSelected(si); setSplitOpen(selected === si ? !splitOpen : true); }} />
                            <Tool icon="trash" title="Take this segment out" danger onClick={() => removeSlot(si)} />
                          </Toolbar>
                          <Knob label="Clip volume" min={GAIN_MIN} max={GAIN_MAX} value={{ db: slot.gainDb, muted: slot.audio === 'mute' }}
                            dimmed={panelNote('clips')}
                            onChange={(v, done) => {
                              if (!done) return;
                              setSlot(si, { gainDb: v.db, audio: v.muted ? 'mute' : slot.audio === 'mute' ? 'keep' : slot.audio });
                              if (v.db !== slot.gainDb) offByHand('clips');
                            }} />
                          <RowMenu title="Segment actions" items={[
                            ...FAST_SPLIT_PARTS.map((n) => ({ label: `Split into ${n} parts`, disabled: !info?.grid.points.length, onClick: () => splitSlot(si, n) })),
                            null,
                            { label: slot.audio === 'mute' ? 'Unmute the clip\'s own sound' : 'Mute the clip\'s own sound', onClick: () => setSlot(si, { audio: slot.audio === 'mute' ? 'keep' : 'mute' }) },
                            null,
                            { label: 'Take this segment out', danger: true, onClick: () => removeSlot(si) },
                          ]} />
                        </div>
                        {info?.unread && <span className="sub fast-note">Reading keyframes…</span>}
                        {info && !info.unread && info.grade !== 'ready' && (
                          <span className="fast-note">
                            <span className="chip warn" title={info.grade === 'coarse' ? 'Cuts can only land this far apart' : 'Too far apart to cut well until prepared'}>
                              Cuts {info.spacingMs ? `every ${(info.spacingMs / 1000).toFixed(0)} s` : 'at the end only'}
                            </span>
                            <button type="button" className="link-btn" disabled={isPreparing || !meta}
                              title="Re-encode this clip once with a keyframe every second. It takes about twice its length"
                              onClick={() => {
                                if (!meta) return;
                                setPreparing((p) => new Set(p).add(meta.assetId));
                                prepare.mutate({ assetId: meta.assetId });
                              }}>{isPreparing ? 'Preparing…' : 'Prepare'}</button>
                          </span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
              {audioDesk()}
            </div>
            <div className="compose-right">
              <div className="card compose-preview">
                <div className={`preview-frame f-${d.format.replace(':', '-')}`}>
                  {/* crossOrigin: the live mix can only hear media served with CORS. */}
                  <video ref={videoRef} className={`preview-video${frontVideo === 0 ? '' : ' standby'}`} playsInline preload="auto" crossOrigin="anonymous"
                    onPlay={onVideoPlay} onPause={onVideoPause} poster={d.slotMeta[0]?.posterUrl ?? undefined} />
                  <video ref={altVideoRef} className={`preview-video${frontVideo === 1 ? '' : ' standby'}`} playsInline preload="auto" crossOrigin="anonymous"
                    onPlay={onVideoPlay} onPause={onVideoPause} />
                  {!previewPlaying && (
                    <button type="button" className="preview-play" aria-label="Play the cut" onClick={playPreview}>
                      <svg viewBox="0 0 16 16" aria-hidden="true"><path d="M5 3.5v9l7-4.5z" /></svg>
                    </button>
                  )}
                </div>
                <PreviewScrubber totalMs={totalMs} position={positionMs} onSeek={seekPreview} />
                <div className="row compose-preview-bar">
                  <button type="button" className="pv-btn" aria-label="Play" title="Play the cut" disabled={!d.slots.length} onClick={playPreview}><Icon name="play" /></button>
                  <button type="button" className="pv-btn" aria-label="Pause" title="Pause" onClick={pausePreview}><Icon name="pause" /></button>
                  <button type="button" className="pv-btn" aria-label="Stop" title="Stop and return to the first frame" onClick={stopPreview}><Icon name="stop" /></button>
                  <button type="button" className="pv-btn pv-render" title="Export without re-encoding the picture: cuts land on keyframes"
                    disabled={!d.slots.length || exportCut.isPending || render.data?.status === 'queued' || render.data?.status === 'rendering'}
                    onClick={() => exportCut.mutate({ draftId: d.id })}><Icon name="export" /> Export</button>
                  {render.data && <RenderPill status={render.data.status} stage={render.data.stage} progress={render.data.progress} etaMs={render.data.etaMs} />}
                  {render.data?.status === 'done' && render.data.publicUrl && (
                    <a className="link-btn" href={render.data.publicUrl} target="_blank" rel="noreferrer">Open</a>
                  )}
                  {previewSlot !== null && <span className="sub">segment {previewSlot + 1} of {d.slots.length}</span>}
                  <span className="sub mono pv-dur">{(totalMs / 1000).toFixed(1)} s</span>
                </div>
                {render.data?.route === 'encode' && render.data.routeNote && <span className="sub fast-note">Encoded in full: {render.data.routeNote}</span>}
                {render.data?.status === 'failed' && render.data.error && <div className="error" role="alert">{render.data.error}</div>}
              </div>
            </div>
          </div>
        )}

        {addOpen && (
          <>
            <button type="button" className="overlay overlay-btn" aria-label="Close" onClick={() => setAddOpen(false)} />
            <div className="modal picker-modal" role="dialog" aria-label="Add a clip">
              <div className="row between"><h2>Add a clip</h2></div>
              {clipPicker}
            </div>
          </>
        )}
        {loadOpen && (
          <>
            <button type="button" className="overlay overlay-btn" aria-label="Close" onClick={() => setLoadOpen(false)} />
            <div className="modal picker-modal" role="dialog" aria-label="Saved edits">
              <div className="row between"><h2>Saved edits</h2></div>
              <div className="picker-list">
                {edits.data?.map((x) => (
                  <button key={x.id} type="button" className={`picker-row${x.id === draftId ? ' on' : ''}`} onClick={() => { setDraftId(x.id); setRenderId(null); setSelected(0); setLoadOpen(false); }}>
                    <span className="dd-label">{x.name ?? 'Untitled edit'}</span>
                    <span className="sub dd-n">{x.slots} segments · {new Date(x.updatedAt).toLocaleDateString()}</span>
                  </button>
                ))}
              </div>
            </div>
          </>
        )}
        {audioPopups()}
      </section>
    </>
  );
}
